/**
 * 域名校验函数（支持强制要求协议前缀）
 * @param {string} domain - 待验证的域名字符串
 * @param {Object} options - 配置选项
 * @param {boolean} options.requireProtocol - 是否强制要求协议前缀(http/https)，默认false
 * @param {Array<string>} options.allowedProtocols - 允许的协议列表，默认['http', 'https']
 * @param {boolean} options.allowSubDomain - 是否允许子域名，默认true
 * @param {boolean} options.allowPath - 是否允许路径，默认false
 * @param {boolean} options.allowPort - 是否允许端口号，默认false
 * @returns {boolean} - 验证结果
 */
const validateDomain = (domain, options = {}) => {
  // 默认配置
  const config = {
    requireProtocol: false,
    allowedProtocols: ['http', 'https'],
    allowSubDomain: true,
    allowPath: false,
    allowPort: false,
    ...options
  }

  if (!domain || typeof domain !== 'string') {
    return false
  }

  // 检查是否包含协议
  const protocolMatch = domain.match(/^([a-z][a-z0-9+\-.]*):\/\/(.*)/i)

  if (config.requireProtocol) {
    // 强制要求协议
    if (!protocolMatch) {
      return false
    }

    const protocol = protocolMatch[1].toLowerCase()
    if (!config.allowedProtocols.includes(protocol)) {
      return false
    }

    // 提取协议后的部分
    domain = protocolMatch[2]
  } else {
    // 如果不强制要求协议，但存在协议，则提取出来
    if (protocolMatch) {
      const protocol = protocolMatch[1].toLowerCase()
      if (config.allowedProtocols.includes(protocol)) {
        domain = protocolMatch[2]
      }
    }
  }

  // 分离端口（如果存在）
  let withoutPort = domain
  let portPart = ''
  if (config.allowPort) {
    const portMatch = domain.match(/^(.*?):(\d{1,5})(\/.*)?$/)
    if (portMatch) {
      const portNum = parseInt(portMatch[2], 10)
      if (portNum < 1 || portNum > 65535) {
        return false // 无效端口
      }
      withoutPort = portMatch[1]
      portPart = ':' + portMatch[2]
    } else {
      // 检查是否有端口但不在末尾
      const portInMiddle = domain.match(/^(.*?):(\d{1,5})$/)
      if (portInMiddle) {
        const portNum = parseInt(portInMiddle[2], 10)
        if (portNum < 1 || portNum > 65535) {
          return false
        }
        withoutPort = portInMiddle[1]
        portPart = ':' + portInMiddle[2]
      }
    }
  }

  // 分离路径（如果存在）
  let domainWithoutPath = withoutPort
  if (config.allowPath) {
    const pathIndex = withoutPort.indexOf('/')
    if (pathIndex !== -1) {
      domainWithoutPath = withoutPort.substring(0, pathIndex)
    }
  }

  // 验证域名部分
  const domainParts = domainWithoutPath.split('.')

  if (config.allowSubDomain) {
    // 允许子域名：至少需要两部分（主域名.顶级域名）
    if (domainParts.length < 2) {
      return false
    }
  } else {
    // 不允许子域名：正好两部分（主域名.顶级域名）
    if (domainParts.length !== 2) {
      return false
    }
  }

  // 验证每个域名部分
  for (let i = 0; i < domainParts.length; i++) {
    const part = domainParts[i]

    // 检查长度和格式
    if (part.length === 0 || part.length > 63) {
      return false
    }

    // 检查格式：只能包含字母、数字、连字符，不能以连字符开始或结束
    if (!/^[a-zA-Z0-9]([a-zA-Z0-9\-]*[a-zA-Z0-9])?$/.test(part)) {
      return false
    }

    // 如果是顶级域名（最后一个部分），长度至少为2
    if (i === domainParts.length - 1 && part.length < 2) {
      return false
    }
  }

  return true
}

/**
 * 专门验证必须带协议的域名
 */
export const isDomainWithProtocol = (domain, protocols = ['http', 'https']) => {
  return validateDomain(domain, {
    requireProtocol: true,
    allowedProtocols: protocols,
    allowSubDomain: true,
    allowPath: false,
    allowPort: false
  })
}

/**
 * 专门验证必须带协议的完整URL
 */
export const isFullUrlWithProtocol = (domain, protocols = ['http', 'https']) => {
  return validateDomain(domain, {
    requireProtocol: true,
    allowedProtocols: protocols,
    allowSubDomain: true,
    allowPath: true,
    allowPort: true
  })
}